import type {Options as VectorSourceOptions} from 'ol/source/Vector';
import VectorSource from 'ol/source/Vector.js';
import FormatGeoJSON from 'ol/format/GeoJSON.js';
import {proj as epsg21781} from '@geoblocks/proj/src/EPSG_21781.js';
import StyleIcon from 'ol/style/Icon.js';
import type {StyleLike} from 'ol/style/Style';
import Style from 'ol/style/Style.js';
import {RESOLUTIONS, Z_INDEXES} from '../constants.ts';
import VectorLayer from 'ol/layer/Vector.js';
import type {Options as VectorLayerOptions} from 'ol/layer/Vector';
import type Feature from 'ol/Feature';
import {ROUTE_LOGOS_BASE_URL} from '../../envs.ts';
import {isWinterLayer} from '../../layers/layerHelpers.ts';
import {getCached} from '../cache-helper.ts';
import {injectDimensions} from '../../svg.ts';

const allowedCategory = (
  category: string,
  res: number,
  selection: string[],
) => {
  const national =
    category === 'national' && !!selection.find((c) => c.includes('national'));
  const regional =
    category === 'regional' && !!selection.find((c) => c.includes('regional'));
  const local =
    category === 'local' &&
    !!selection.find((c) => c.includes('lokal') || isWinterLayer(c));
  const handicap =
    category === 'handicap' && !!selection.find((c) => c.includes('handicap'));

  // If you change this part, you must change also the values in api/route_logos/generate-logos.js
  if (res <= 20) {
    return national || regional || local || handicap;
  } else if (res <= 100) {
    return national || regional;
  } else {
    return national;
  }
};

const normalizeResolution = (resolution: number) => {
  for (let i = RESOLUTIONS.length - 1; i >= 0; i--) {
    if (resolution <= RESOLUTIONS[i]) {
      return RESOLUTIONS[i];
    }
  }
  return RESOLUTIONS[RESOLUTIONS.length - 1];
};

export class LogoSource extends VectorSource {
  constructor(filename: string, options?: VectorSourceOptions<Feature>) {
    super(
      Object.assign(
        {},
        {
          format: new FormatGeoJSON({
            dataProjection: epsg21781,
          }),
          url: `${ROUTE_LOGOS_BASE_URL}${filename}`,
        },
        options,
      ),
    );
  }
}

export class LogoLayer extends VectorLayer<VectorSource> {
  private styleCache: {[key: string]: {[key: string]: Style | Style[]}} = {};
  private mappingsInitialized = false;
  private categoryByRoute: {[key: string]: string} = {};

  constructor(filename: string, name: string, options?: VectorLayerOptions) {
    super(
      Object.assign(
        {},
        {
          zIndex: Z_INDEXES.ROUTE_LOGOS,
          visible: false,
          source: getCached(filename, () => new LogoSource(filename)),
          properties: {name: `logo_${name}`},
        },
        options,
      ),
    );
  }

  private initializeMappings() {
    if (!this.mappingsInitialized) {
      this.getSource()
        .getFeatures()
        .forEach((feature) => {
          const route = feature.get('route');
          this.categoryByRoute[route] = feature.get('category');
        });
      this.mappingsInitialized = true;
    }
  }

  private getLogoUrlToDisplay(
    properties: {[key: string]: any},
    resolution: number,
    selection: string[],
  ) {
    resolution = normalizeResolution(resolution);
    const displayable = properties['resolutions'][resolution];
    // If alternates is defined at this resolution, then there is potentially something to display
    if (displayable) {
      const defaultRoute = properties['route'];
      const defaultUrl = properties['logo_svg'] || properties['logo'];
      // If the route of the dot can be displayed, then use it
      if (
        allowedCategory(
          this.categoryByRoute[defaultRoute],
          resolution,
          selection,
        )
      ) {
        return defaultUrl;
      }
    }

    // There is no match.
    return null;
  }

  private getOrCreateStyleForLogo(
    logo: string,
    resolution: number,
    layer: VectorLayer<VectorSource>,
  ) {
    this.styleCache[logo] = this.styleCache[logo] || {};
    const logoSize = getLogoSize(resolution);
    const style = this.styleCache[logo][logoSize];
    if (!style) {
      createLogoStyle(logo, resolution).then((style) => {
        this.styleCache[logo][logoSize] = style;
        layer.changed();
      });
    }
    return style;
  }

  updateStyles(selection: string[]) {
    const createStyle = (feature: Feature, resolution: number) => {
      this.initializeMappings();
      const logoUrl = this.getLogoUrlToDisplay(
        feature.getProperties(),
        resolution,
        selection,
      );
      return logoUrl
        ? this.getOrCreateStyleForLogo(logoUrl, resolution, this)
        : Object.seal([]);
    };

    this.setStyle(<StyleLike>createStyle);
  }
}

function getLogoSize(resolution: number): number {
  return resolution <= 250 ? 28 : 20;
}

async function createLogoStyle(
  logo: string,
  resolution: number,
): Promise<Style> {
  const src = await injectDimensions(logo);
  const sizeOriginal = 150;
  // Here we scale down the original size to either 28px for all resolutions
  // including and below 250 or to 20px for the two largest resolutions.
  // If you change the value here, also change the value in getLogoRadius()
  // in api/route_logos/generate-logos.js.
  const sizeScaled = getLogoSize(resolution);
  return new Style({
    image: new StyleIcon({
      scale: sizeScaled / sizeOriginal,
      src: src,
    }),
  });
}
